import { TypeFiesta, TypeInsignia } from '@at/design-system-foundation'
import React from 'react'
import { Button } from '@at/design-system-elements'
import { useForm } from '../../forms/hocs/form.context'
import { ActionWrapper, MessageWrapper } from '../info-banner/info-banner.styles'
import { StyledTertiaryButton } from '../../patterns/search-facets/search-facets.styles'
import { dataAttrGUI } from '@at/formatter-utils/lib/data-attr-utils/data-attr-utils'
import { ZeroResultsBannerWrapper, ZeroResultsBannerWrapperModal } from './zero-results.styles'
import { useSearchViewState } from '../../contexts/search-view-state-context'
import { useTrackingHandlers } from '../../hooks/tracking/tracking-handlers'
import { FilterName } from '../../__generated__/fed-graphql-types'
import { Selected } from '../../forms/hocs/selected-options'

function getFiltersToUndo(lastSelectedFilter: FilterName, selectedMap: Map<FilterName, Selected>) {
    if (
        lastSelectedFilter === FilterName.price_search_type &&
        selectedMap.get(FilterName.price_search_type)?.values.includes('monthly')
    ) {
        return [
            { filter: FilterName.price_search_type, selected: [undefined] },
            { filter: FilterName.deposit, selected: [undefined] },
            { filter: FilterName.term, selected: [undefined] },
            { filter: FilterName.yearly_mileage, selected: [undefined] },
        ]
    }

    return [{ filter: lastSelectedFilter, selected: [undefined] }]
}

export const ZeroResultsMessageModal = () => {
    const { isLastSelected, updateSelected, resetSearch, filterNumber, selectedMap } = useForm()

    const { trackSearchFilterReset } = useTrackingHandlers()

    const optionPhrase = `${
        isLastSelected
            ? 'It’s easy to fix, try these options:'
            : 'Clear your filters and try using different options to see more search results'
    }`

    const undoLastFilter = isLastSelected
        ? () => {
              updateSelected(isLastSelected.facet, getFiltersToUndo(isLastSelected?.filter, selectedMap))
          }
        : () => {}

    return (
        <ZeroResultsBannerWrapperModal as="div" role="alert">
            <MessageWrapper>
                <TypeInsignia as="h3" weight="400">
                    There are no vehicles that match your filters
                </TypeInsignia>
                <TypeFiesta as="p">{optionPhrase}</TypeFiesta>
            </MessageWrapper>
            <ActionWrapper>
                {isLastSelected ? (
                    <Button
                        displayType="primary"
                        text={'Undo last filter'}
                        onInteraction={() => {
                            undoLastFilter()
                        }}
                        trackingCallback={trackSearchFilterReset}
                        attributes={{
                            autoFocus: true,
                        }}
                    />
                ) : null}
                <StyledTertiaryButton
                    className="search-form-reset"
                    displayType="tertiary"
                    text={`Clear all filters (${filterNumber})`}
                    type="button"
                    onInteraction={() => {
                        resetSearch()
                    }}
                    trackingCallback={trackSearchFilterReset}
                    attributes={{ [`${dataAttrGUI}`]: 'search-reset-button' }}
                    $isMobile={true}
                    underline={true}
                />
            </ActionWrapper>
        </ZeroResultsBannerWrapperModal>
    )
}

export const ZeroResultsMessage = () => {
    const { resetSearch } = useForm()
    const { toggleSearchFilter } = useSearchViewState()

    const { trackSearchFilterReset } = useTrackingHandlers()

    return (
        <ZeroResultsBannerWrapper as="div" role="alert">
            <MessageWrapper>
                <TypeInsignia as="h3" weight="400">
                    There are no vehicles that match your filters
                </TypeInsignia>
                <TypeFiesta as="p">{`No problem, try these options:`}</TypeFiesta>
            </MessageWrapper>
            <ActionWrapper>
                <Button
                    displayType="primary"
                    text={'Search all cars'}
                    onInteraction={() => {
                        resetSearch()
                    }}
                    trackingCallback={trackSearchFilterReset}
                    attributes={{
                        autoFocus: true,
                    }}
                />
                <StyledTertiaryButton
                    className="search-form-reset"
                    displayType="tertiary"
                    text={'Refine search'}
                    type="button"
                    onInteraction={() => {
                        toggleSearchFilter(true)
                    }}
                    trackingCallback={trackSearchFilterReset}
                    attributes={{ [`${dataAttrGUI}`]: 'search-reset-button' }}
                    $isMobile={true}
                    underline={true}
                />
            </ActionWrapper>
        </ZeroResultsBannerWrapper>
    )
}
